import {
    WILDCARD_CAPACITY,
    isBookableCapacity,
    resolveBookableCapacities,
    resolveChargeDivisorCapacities,
} from '../../../utils/sport-capacity';
import { SportType } from '../../../models/court/dto/court.dto';

/**
 * Returns true when any of the resolved prices is missing or not a valid
 * non-negative number.
 *
 * The price field parses to `undefined` when left empty or non-numeric
 * (see AddAvailabilitiesForm: `isNaN(parsed) ? undefined : parsed`). Submitting
 * such a payload makes the API reject the batch with a 400, so we catch it in
 * the form first and show a clear message instead of a generic error toast.
 *
 * `0` is a valid price (free court). Pass the already-resolved price for each
 * pricing (i.e. apply any `?? fallback` before calling).
 */
export function hasInvalidAvailabilityPrice(
    prices: (number | undefined | null)[],
): boolean {
    return prices.some(
        (price) =>
            price === undefined ||
            price === null ||
            Number.isNaN(price) ||
            price < 0,
    );
}

/** One pricing configuration as the availability form holds it. */
export type AvailabilityPricingConfig = {
    sport?: unknown;
    slotDurationInMs?: unknown;
    /** Absent means "prices every capacity" — the wildcard row. */
    capacity?: number | null;
};

/**
 * "This row prices every capacity."
 *
 * The form writes `undefined` for an empty field, but the column default and
 * the value a saved template hands back is the `-1` sentinel. Both mean the
 * same thing to the server, so every check here has to see them the same way.
 */
function isWildcardCapacity(capacity: number | null | undefined): boolean {
    return (
        capacity === undefined ||
        capacity === null ||
        capacity === WILDCARD_CAPACITY
    );
}

/**
 * Why a capacity a form holds cannot be saved. `null` when it is fine.
 *
 * Mirrors `assertValidAvailabilityPricingCapacities` on the API
 * (`api/src/court/service/availabilities/assert-valid-availability-prices.ts`),
 * which rejects the same shapes with a 400 — the dashboard had no guard at
 * all, so an operator only found out after the whole batch bounced with a
 * generic error toast.
 *
 * - `invalid` — not a whole number of players. The capacity field is a
 *   `NumberInput`, which yields `NaN` for any non-numeric text. **Blocking**:
 *   the API rejects it.
 * - `duplicate` — two configurations in the SAME availability block share a
 *   (sport, duration, capacity) triple, so which one prices a booking is
 *   ambiguous. **Blocking**: the API rejects it. Scoped to capacity-specific
 *   rows exactly like the API guard and the partial unique index — duplicate
 *   *wildcard* rows have always been allowed and are resolved deterministically
 *   server-side, and rejecting them would break saves that work today.
 * - `unbookable` — a whole number, but outside the `[1, 20]` range every money
 *   path enforces, so no booking can ever select the row. **Blocking**: the API
 *   rejects it too (`assertValidAvailabilityPricingCapacities` 400s on exactly
 *   this range), so the save this used to wave through failed anyway — and the
 *   availability modal's error toast has no way to say why. Blocking here costs
 *   the operator nothing they could otherwise have saved and replaces "Error"
 *   with the offending number. The wildcard sentinel (`-1`) is NOT this: it is
 *   normalised away below, exactly as the API normalises it, so a row loaded
 *   back from a template that stores the sentinel is not mistaken for a dead
 *   row and does not block a save the server would accept.
 */
export type AvailabilityCapacityIssue = {
    kind: 'invalid' | 'unbookable' | 'duplicate';
    capacity: number;
};

export const BLOCKING_CAPACITY_ISSUE_KINDS: AvailabilityCapacityIssue['kind'][] =
    ['invalid', 'unbookable', 'duplicate'];

/**
 * Grouping matters: pass ONE array per availability block. Overlapping
 * availabilities may legitimately repeat a triple, and the API groups the same
 * way — a flattened list would reject schedules the server accepts.
 */
export function findAvailabilityCapacityIssues(
    configurationGroups: AvailabilityPricingConfig[][],
    { minCapacity, maxCapacity }: { minCapacity: number; maxCapacity: number },
): AvailabilityCapacityIssue[] {
    const issues: AvailabilityCapacityIssue[] = [];

    for (const group of configurationGroups) {
        const seen = new Set<string>();

        for (const config of group) {
            const { capacity } = config;

            // Empty means the wildcard row: it prices every capacity and is
            // the shape every pre-multi-capacity row already has. `-1` is the
            // stored form of the same thing (the column default), so a
            // configuration read back from a saved template must normalise to
            // it — the API does (`capacity ?? WILDCARD_CAPACITY`, then accepts
            // `-1`), and flagging it would refuse a payload the server takes.
            if (isWildcardCapacity(capacity)) {
                continue;
            }

            if (!Number.isInteger(capacity)) {
                issues.push({ kind: 'invalid', capacity });
                continue;
            }

            if (capacity < minCapacity || capacity > maxCapacity) {
                issues.push({ kind: 'unbookable', capacity });
                // Still de-duplicated below: an unbookable row repeated twice
                // is also a duplicate the API would reject.
            }

            const key = `${String(config.sport)}|${String(
                config.slotDurationInMs,
            )}|${capacity}`;

            if (seen.has(key)) {
                issues.push({ kind: 'duplicate', capacity });
            }

            seen.add(key);
        }
    }

    return issues;
}

/** True when any issue would make the API reject the whole batch. */
export function hasBlockingCapacityIssue(
    issues: AvailabilityCapacityIssue[],
): boolean {
    return issues.some((issue) =>
        BLOCKING_CAPACITY_ISSUE_KINDS.includes(issue.kind),
    );
}

/**
 * A price row and the organization's capacity offering disagreeing.
 *
 * These are the two states nobody could see: the availability screen and the
 * game-capacity-options screen each validate their own side and never consult
 * the other, so an operator produces both by doing something entirely
 * reasonable on one screen.
 *
 * - `not-bookable` — a price is configured for a capacity no booking can
 *   request, because the organization does not offer it (and it is not the
 *   sport's legacy capacity, which stays accepted for old clients). The row is
 *   money nobody can spend. In its worst form — capacity prices, **no**
 *   configured options, and no wildcard row — the sport is not bookable on that
 *   slot **at all**: the grid renders a dash, the picker returns nothing, and a
 *   client that requests the priced capacity anyway gets a 400.
 * - `uniform-price` — the reverse: several capacities are offered and the slot
 *   prices them all with a single capacity-agnostic row. Every size costs the
 *   operator's one price, which may well be intended — but a PERCENTAGE
 *   membership discount covers exactly one seat, so it is worth twice as much
 *   at capacity 2 as at capacity 4 on the same court in the same hour, and the
 *   picker advertises both totals. The operator currently has no way to see
 *   that, which is what makes it worth a line.
 *
 * Both are **warnings**. The save is valid and the API accepts it; refusing it
 * would strand any operator whose year-long template already contains one,
 * since the batch writers replace the whole window on every save.
 */
export type CapacityOfferingMismatch =
    | { kind: 'not-bookable'; sport: unknown; capacity: number; bookable: number[] }
    | { kind: 'uniform-price'; sport: unknown; capacities: number[] };

/**
 * Cross-checks the prices a form holds against the capacities the organization
 * actually offers.
 *
 * `bookableCapacitiesForSport` answers "which capacities can a booking for this
 * sport be made at" — pass `undefined` while the offering is still loading or
 * unavailable, and nothing is reported: a warning derived from data we do not
 * have is worse than no warning.
 */
export function findCapacityOfferingMismatches(
    configurationGroups: AvailabilityPricingConfig[][],
    {
        bookableCapacitiesForSport,
        offeredCapacitiesForSport,
    }: {
        bookableCapacitiesForSport?: (sport: unknown) => number[];
        offeredCapacitiesForSport?: (sport: unknown) => number[];
    },
): CapacityOfferingMismatch[] {
    if (!bookableCapacitiesForSport || !offeredCapacitiesForSport) {
        return [];
    }

    const mismatches: CapacityOfferingMismatch[] = [];

    for (const group of configurationGroups) {
        const bySlot = new Map<
            string,
            {
                sport: unknown;
                capacities: number[];
                hasWildcard: boolean;
            }
        >();

        for (const config of group) {
            const key = `${String(config.sport)}|${String(
                config.slotDurationInMs,
            )}`;
            const entry = bySlot.get(key) ?? {
                sport: config.sport,
                capacities: [],
                hasWildcard: false,
            };

            if (isWildcardCapacity(config.capacity)) {
                entry.hasWildcard = true;
            } else if (Number.isInteger(config.capacity)) {
                entry.capacities.push(config.capacity as number);
            }

            bySlot.set(key, entry);
        }

        for (const entry of bySlot.values()) {
            const bookable = bookableCapacitiesForSport(entry.sport);

            for (const capacity of entry.capacities) {
                if (!bookable.includes(capacity)) {
                    mismatches.push({
                        kind: 'not-bookable',
                        sport: entry.sport,
                        capacity,
                        bookable,
                    });
                }
            }

            // Only meaningful once the player is given a choice: a single
            // offered capacity has no picker and therefore no lever. An
            // out-of-range option is not a choice either — the server drops it
            // from the player-facing offering.
            const offered = offeredCapacitiesForSport(entry.sport).filter(
                isBookableCapacity,
            );
            if (
                offered.length > 1 &&
                entry.hasWildcard &&
                entry.capacities.length === 0
            ) {
                mismatches.push({
                    kind: 'uniform-price',
                    sport: entry.sport,
                    capacities: [...new Set(offered)].sort((a, b) => a - b),
                });
            }
        }
    }

    return mismatches;
}

/**
 * Turns the organization's configured capacity options into the two lookups
 * `findCapacityOfferingMismatches` needs.
 *
 * Returns an EMPTY object while `options` is `undefined` (in flight, or the
 * query failed), which switches the cross-check off rather than reporting every
 * capacity-specific row as unbookable the moment a request is slow.
 */
export function buildCapacityOfferingLookups(
    options: { sport: unknown; capacity: number }[] | undefined,
): {
    bookableCapacitiesForSport?: (sport: unknown) => number[];
    offeredCapacitiesForSport?: (sport: unknown) => number[];
} {
    if (!options) {
        return {};
    }

    const bySport = new Map<string, number[]>();

    for (const option of options) {
        const key = String(option.sport);
        bySport.set(key, [...(bySport.get(key) ?? []), option.capacity]);
    }

    const offeredCapacitiesForSport = (sport: unknown) =>
        bySport.get(String(sport)) ?? [];

    return {
        offeredCapacitiesForSport,
        bookableCapacitiesForSport: (sport: unknown) =>
            resolveBookableCapacities(
                sport as SportType,
                offeredCapacitiesForSport(sport),
            ),
    };
}

/**
 * Sport + duration combinations where a capacity a booking can actually be
 * made at has no price — no exact row, and no capacity-agnostic row to fall
 * back on.
 *
 * Price selection prefers the row matching the requested capacity and falls
 * back to the wildcard row (`capacity` empty); with neither, the quote 404s.
 * So a block that prices Padel-2 and nothing else turns every OTHER bookable
 * capacity into "no price on this slot" — including the sport's own legacy
 * capacity, which the server accepts from every client whatever the
 * organization has configured (`resolveBookingCapacity` rule 3). The save is
 * perfectly valid, the operator just cannot see the hole from the form.
 *
 * ## Why this is not simply "there is no wildcard row"
 *
 * It used to be, and that fired on the CORRECT configuration. An operator who
 * prices Padel at 2 and at 4 — the exact setup this whole feature exists to
 * sell, with the organization offering {2, 4} and every one of them priced —
 * has no wildcard row and no hole, and got the warning on every save anyway.
 * A warning that is permanently on for the intended state is not a warning:
 * the operators most invested in multi-capacity pricing were the ones being
 * trained to scroll past the box that would one day name a real gap.
 *
 * So the question asked is the one that has an answer: which capacities can a
 * booking be made at, and which of those has nothing to charge? Empty means the
 * block prices everything it needs to, wildcard row or not.
 *
 * The set asked about is `resolveChargeDivisorCapacities`: every capacity a
 * charge off this slot can be looked up at. It was `resolvePricingCapacities`
 * between Run 65 and Run 147, on the reasoning that D30 prices a pre-picker
 * client's own capacity at the sport's legacy one, so that value "needs no row
 * of its own". It needs one for everybody who JOINS: D30 leaves the seat count
 * as the client asked for it, `Game.capacity` is stamped with it, and
 * `gameJoiningCapacity` uses it to select the row and divide the share. A
 * squash block priced at capacity 2 with no wildcard row therefore prices the
 * pre-picker booking and then has nothing at all for the 4-seat game it just
 * created — the quote 404s for every joiner, which is exactly the hole this
 * warning exists to name. The API's own coverage advisor
 * (`unpriced-bookable-capacities.ts`) has always asked the wider question; this
 * is that disagreement resolved in its favour.
 *
 * Still NOT as wide as every accepted capacity in the abstract: with options
 * configured the two sets coincide, because rule 3 rejects a capacity outside
 * the offered set, so an operator pricing Padel at 2 and 4 is warned about
 * nothing — the state this narrowing was introduced to protect is unchanged.
 *
 * `offeredCapacitiesForSport` comes from the organization's configured options.
 * When it is absent — the offering query is in flight, or failed — this falls
 * back to the unconfigured answer, which needs no query to know. That is
 * narrower than the truth for a configured organization rather than wider: a
 * missed warning, never an invented one.
 */
export function findSlotsWithoutCapacityFallback(
    configurationGroups: AvailabilityPricingConfig[][],
    {
        offeredCapacitiesForSport,
    }: {
        offeredCapacitiesForSport?: (sport: unknown) => number[];
    } = {},
): {
    sport: unknown;
    slotDurationInMs: unknown;
    capacities: number[];
    unpriced: number[];
}[] {
    const gaps: {
        sport: unknown;
        slotDurationInMs: unknown;
        capacities: number[];
        unpriced: number[];
    }[] = [];

    const pricedCapacitiesFor = (sport: unknown): number[] =>
        resolveChargeDivisorCapacities(
            sport as SportType,
            offeredCapacitiesForSport?.(sport) ?? [],
        );

    for (const group of configurationGroups) {
        const bySlot = new Map<
            string,
            {
                sport: unknown;
                slotDurationInMs: unknown;
                capacities: number[];
                hasWildcard: boolean;
            }
        >();

        for (const config of group) {
            const key = `${String(config.sport)}|${String(
                config.slotDurationInMs,
            )}`;
            const entry = bySlot.get(key) ?? {
                sport: config.sport,
                slotDurationInMs: config.slotDurationInMs,
                capacities: [],
                hasWildcard: false,
            };

            if (isWildcardCapacity(config.capacity)) {
                entry.hasWildcard = true;
            } else if (Number.isInteger(config.capacity)) {
                entry.capacities.push(config.capacity as number);
            }

            bySlot.set(key, entry);
        }

        for (const entry of bySlot.values()) {
            // A wildcard row prices every capacity, so there is no hole to
            // report; a block with no capacity-specific row at all is the
            // pre-feature shape and is not this check's business either.
            if (entry.hasWildcard || entry.capacities.length === 0) {
                continue;
            }

            const priced = new Set(entry.capacities);
            const unpriced = [
                ...new Set(
                    pricedCapacitiesFor(entry.sport).filter(
                        (capacity) => !priced.has(capacity),
                    ),
                ),
            ].sort((a, b) => a - b);

            if (unpriced.length === 0) {
                continue;
            }

            gaps.push({
                sport: entry.sport,
                slotDurationInMs: entry.slotDurationInMs,
                capacities: [...entry.capacities].sort((a, b) => a - b),
                unpriced,
            });
        }
    }

    return gaps;
}
